/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { errorApiRef, useApi } from '@backstage/core-plugin-api'
import { useEntity } from '@backstage/plugin-catalog-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import useAsyncRetry from 'react-use/lib/useAsyncRetry'

import { circleCIApiRef, Pipeline, ProjectSlug, Workflow } from '../api'
import { CIRCLECI_ANNOTATION } from '../constants'

/** A row in the pipeline-grouped view: a pipeline paired with one of its workflows. */
export type PipelineRowData = {
  rowId: string
  pipeline: Pipeline
  workflow: Workflow
}

export const useProjectSlugFromEntity = (): ProjectSlug => {
  const { entity } = useEntity()
  const raw = entity.metadata.annotations?.[CIRCLECI_ANNOTATION] ?? ''
  // Memoised so downstream `useCallback`/`useAsyncRetry` deps stay stable —
  // otherwise the builds page enters an infinite fetch loop.
  return useMemo(() => {
    const [vcs = '', owner = '', repo = ''] = raw.split('/')
    return { vcs, owner, repo }
  }, [raw])
}

/**
 * One slot in the cursor-pagination cache. `token` is the v2 `page-token` we
 * pass when fetching page N; `rows` and `nextToken` are populated after the
 * fetch lands and let subsequent visits to the page short-circuit.
 */
type PageSlot = {
  token: string | undefined
  rows?: PipelineRowData[]
  nextToken?: string | null
}

export function useBuilds() {
  const slug = useProjectSlugFromEntity()
  const api = useApi(circleCIApiRef)
  const errorApi = useApi(errorApiRef)

  const projectName = `${slug.owner}/${slug.repo}`

  // Cursor + result cache. Held in a ref so the post-fetch write doesn't
  // invalidate `useAsyncRetry`'s dep array.
  const cacheRef = useRef<PageSlot[]>([{ token: undefined }])
  const [page, setPage] = useState(0)
  const [pageCount, setPageCount] = useState(1)
  const [refreshKey, setRefreshKey] = useState(0)

  const { vcs, owner, repo } = slug

  const { loading, value, error } = useAsyncRetry(async () => {
    if (!owner || !repo || !vcs) {
      throw new Error('Missing CircleCI annotation on entity')
    }

    const cached = cacheRef.current[page]
    if (cached?.rows) return cached.rows

    const pipelinesResp = await api.getPipelines({ vcs, owner, repo }, { pageToken: cached?.token })
    const rows: PipelineRowData[] = []
    let firstWorkflowError: unknown

    // Fan-out: one workflow request per pipeline. Partial failures are
    // tolerated so a single bad pipeline doesn't blank the page; we surface
    // the first failure through errorApi so rate-limit / network problems
    // aren't silently swallowed.
    await Promise.all(
      pipelinesResp.items.map(async pipeline => {
        try {
          const wfResp = await api.getWorkflowsForPipeline(pipeline.id)
          for (const workflow of wfResp.items) {
            rows.push({ rowId: `${pipeline.id}:${workflow.id}`, pipeline, workflow })
          }
        } catch (err) {
          if (!firstWorkflowError) firstWorkflowError = err
        }
      })
    )

    if (firstWorkflowError) {
      errorApi.post(firstWorkflowError as Error)
    }

    // Fan-out collapses ordering — re-sort newest first.
    rows.sort((a, b) => {
      const at = new Date(a.workflow.created_at).getTime()
      const bt = new Date(b.workflow.created_at).getTime()
      if (bt !== at) return bt - at
      return b.pipeline.number - a.pipeline.number
    })

    const nextToken = pipelinesResp.next_page_token
    cacheRef.current[page] = { token: cached?.token, rows, nextToken }

    if (nextToken) {
      if (!cacheRef.current[page + 1]) {
        cacheRef.current[page + 1] = { token: nextToken }
      }
      setPageCount(prev => Math.max(prev, page + 2))
    } else if (cacheRef.current.length > page + 1) {
      cacheRef.current.length = page + 1
      setPageCount(page + 1)
    }

    return rows
  }, [page, refreshKey, vcs, owner, repo, api, errorApi])

  const hasNextPage = pageCount > page + 1
  const hasPreviousPage = page > 0

  const goToNextPage = useCallback(() => {
    setPage(p => p + 1)
  }, [])

  const goToPreviousPage = useCallback(() => {
    setPage(p => Math.max(0, p - 1))
  }, [])

  const retry = useCallback(() => {
    cacheRef.current = [{ token: undefined }]
    setPageCount(1)
    setPage(0)
    setRefreshKey(k => k + 1)
  }, [])

  return [
    { loading, rows: value ?? [], projectName, page, error, hasNextPage, hasPreviousPage },
    { retry, goToNextPage, goToPreviousPage, setPage },
  ] as const
}
